/**
 * Settings, You: "I'm away" (availability.ts). While a person is away, the people who cover them under a team's
 * delegation rules may answer the questions a Team Work session asks them. Calm: rows in one group, each change
 * kept as it is made (a note when its field is left).
 */

import { isAwayNow, type Availability } from '@aioe/shared';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { dateTimeWords, useLoad } from '../hooks.ts';
import { ErrorNote, ListBlock, ListGroup, ListRow, selectClass, Toggle } from './ui.tsx';

/** `YYYY-MM-DD` in the person's own time zone, for the date field. */
const dayOf = (iso: string | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
/** Back on the morning of that day: away until the start of it, in the person's time zone. */
const untilOf = (day: string): string | undefined => {
  if (!day) return undefined;
  const d = new Date(`${day}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

export function AvailabilityPanel() {
  const loaded = useLoad(() => routes.myAvailability().catch(() => undefined), []);
  if (!loaded.data) return null;
  return <AvailabilityForm a={loaded.data} reload={loaded.reload} />;
}

function AvailabilityForm({ a, reload }: { a: Availability; reload: () => void }) {
  const awayNow = isAwayNow(a);
  const [away, setAway] = useState(awayNow);
  const [day, setDay] = useState(awayNow ? dayOf(a.until) : '');
  const [note, setNote] = useState(awayNow ? (a.note ?? '') : '');
  const [error, setError] = useState<string>();
  const today = dayOf(new Date().toISOString());
  const keep = async (next: { away: boolean; day: string; note: string }) => {
    setError(undefined);
    try {
      const until = next.away ? untilOf(next.day) : undefined;
      await routes.setMyAvailability({ away: next.away, ...(until ? { until } : {}), ...(next.away && next.note.trim() ? { note: next.note.trim() } : {}) });
      reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <ListGroup id="away" description="While you are away, the people who cover you in a team can answer the questions its agents ask you.">
      <ListRow
        title="I'm away"
        description={awayNow ? (a.until ? `You are marked away until ${dateTimeWords(a.until)}.` : 'You are marked away until you say you are back.') : 'Turn this on when you leave; turn it off when you are back.'}
        control={
          <Toggle
            label="I'm away"
            checked={away}
            onChange={(v) => {
              setAway(v);
              void keep({ away: v, day: v ? day : '', note: v ? note : '' });
            }}
          />
        }
      />
      {away ? (
        <ListRow
          title="Back on"
          htmlFor="aioe-away-until"
          description={day ? 'You show as back from the start of that day.' : 'Leave it empty to stay away until you turn this off.'}
          control={
            <input
              id="aioe-away-until"
              type="date"
              className={`${selectClass} w-44`}
              min={today}
              value={day}
              onChange={(e) => {
                setDay(e.target.value);
                void keep({ away, day: e.target.value, note });
              }}
            />
          }
        />
      ) : null}
      {away ? (
        <ListRow
          title="A note for your colleagues"
          htmlFor="aioe-away-note"
          description="Who covers what while you are gone. Kept when you leave the field."
          control={
            <input
              id="aioe-away-note"
              className={`${selectClass} w-72 max-w-full`}
              maxLength={200}
              value={note}
              placeholder="On leave; Dan covers billing questions"
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => {
                if (note.trim() !== (a.note ?? '')) void keep({ away, day, note });
              }}
            />
          }
        />
      ) : null}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
